import {
  Paper,
  Typography,
  Box,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import type { InflowChannelRow } from '../../hooks/useInflowStats';
import { getInflowDescription, isAuxiliaryBucket } from '../../../../constants/inflowChannels';

interface Props {
  rows: InflowChannelRow[];
}

const COLUMN_COUNT = 7;

/** 그룹 구분 헤더 행 */
const GroupHeaderRow = ({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) => (
  <TableRow>
    <TableCell colSpan={COLUMN_COUNT} sx={{ py: 1.1, bgcolor: '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
      <Stack direction="row" alignItems="center" spacing={0.8}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: '#64748b', '& svg': { fontSize: 15 } }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#475569' }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>
          — {desc}
        </Typography>
      </Stack>
    </TableCell>
  </TableRow>
);

/** 채널 집계 데이터 행 */
const ChannelRow = ({ row }: { row: InflowChannelRow }) => (
  <TableRow hover>
    <TableCell sx={{ py: 1.2, minWidth: 260 }}>
      <Chip
        label={row.label}
        size="small"
        sx={{
          fontWeight: 700, fontSize: 12, height: 24,
          color: row.color, bgcolor: `${row.color}14`,
          border: `1px solid ${row.color}30`,
        }}
      />
      <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.5, lineHeight: 1.4 }}>
        {getInflowDescription(row.bucket)}
      </Typography>
    </TableCell>
    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13 }}>
      {row.count.toLocaleString()}건
    </TableCell>
    <TableCell align="right">
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
        <Box sx={{
          width: { xs: 48, md: 76 }, height: 6, borderRadius: 3,
          bgcolor: '#f1f5f9', overflow: 'hidden', flexShrink: 0,
        }}>
          <Box sx={{
            width: `${Math.min(row.sharePct, 100)}%`, height: '100%', borderRadius: 3,
            background: `linear-gradient(90deg, ${row.color}b3, ${row.color})`,
          }} />
        </Box>
        <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 700, minWidth: 44, textAlign: 'right' }}>
          {row.sharePct}%
        </Typography>
      </Stack>
    </TableCell>
    <TableCell align="right" sx={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
      {row.completed.toLocaleString()}
    </TableCell>
    <TableCell align="right" sx={{ fontSize: 13, color: '#2563eb', fontWeight: 600 }}>
      {row.inProgress.toLocaleString()}
    </TableCell>
    <TableCell align="right" sx={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
      {row.returned.toLocaleString()}
    </TableCell>
    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
      {row.totalPriceOffer > 0 ? `${row.totalPriceOffer.toLocaleString()}원` : '-'}
    </TableCell>
  </TableRow>
);

/**
 * 📈 채널별 상세 집계 표 + 엑셀 내보내기
 * - '온라인 고객 유입'(마케팅 성과 대상)과 '추적 대상 외'(미수집/수동등록)를 그룹으로 분리
 * - 각 분류에 한 줄 설명을 함께 표시해 용어만으로도 의미가 읽히도록 구성
 */
const InflowChannelTable = ({ rows }: Props) => {
  // 그룹 분리 (정렬 순서는 유지 — 건수 desc)
  const onlineRows = rows.filter((r) => !isAuxiliaryBucket(r.bucket));
  const auxiliaryRows = rows.filter((r) => isAuxiliaryBucket(r.bucket));

  const handleDownloadExcel = () => {
    const exportData = rows.map((row) => ({
      구분: isAuxiliaryBucket(row.bucket) ? '추적 대상 외' : '온라인 고객 유입',
      채널: row.label,
      설명: getInflowDescription(row.bucket),
      건수: row.count,
      '비중(%)': row.sharePct,
      완료: row.completed,
      진행중: row.inProgress,
      반송: row.returned,
      검수금액합계: row.totalPriceOffer,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '유입경로 통계');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    saveAs(blob, `${yyyy}_${mm}-${dd}_유입경로통계.xlsx`);
  };

  return (
    <Paper elevation={0} sx={{
      p: { xs: 2, md: 3 }, borderRadius: 3,
      border: '1px solid #e5e7eb', height: '100%',
      display: 'flex', flexDirection: 'column',
    }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography fontWeight={700} sx={{ fontSize: '0.95rem', color: '#0f172a' }}>
          채널별 상세 집계
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadExcel}
          disabled={rows.length === 0}
          sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          Excel
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
        건수 · 비중 · 진행 상태 · 검수금액 합계 — 마케팅 성과는 [온라인 고객 유입] 그룹 기준으로 판단하세요
      </Typography>

      <Box sx={{ flex: 1, overflowX: 'auto' }}>
        <Table size="small" aria-label="채널별 유입 집계 표">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f9fafc' }}>
              {['채널', '건수', '비중', '완료', '진행중', '반송', '검수금액 합계'].map((label) => (
                <TableCell
                  key={label}
                  align={label === '채널' ? 'left' : 'right'}
                  sx={{
                    fontWeight: 700, fontSize: 13, color: '#334155',
                    borderBottom: '1.5px solid #e7ebf0', whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* ── 그룹 1: 온라인 고객 유입 (마케팅 성과 대상) */}
            {onlineRows.length > 0 && (
              <GroupHeaderRow
                icon={<StorefrontIcon />}
                title="온라인 고객 유입"
                desc="고객이 웹에서 직접 접속해 신청한 건 (채널별 성과)"
              />
            )}
            {onlineRows.map((row) => (
              <ChannelRow key={row.bucket} row={row} />
            ))}

            {/* ── 그룹 2: 추적 대상 외 (참고용) */}
            {auxiliaryRows.length > 0 && (
              <GroupHeaderRow
                icon={<InfoOutlinedIcon />}
                title="추적 대상 외 (참고)"
                desc="유입 정보가 없거나 관리자가 등록한 건 — 마케팅 성과에서 제외"
              />
            )}
            {auxiliaryRows.map((row) => (
              <ChannelRow key={row.bucket} row={row} />
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    표시할 데이터가 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

export default InflowChannelTable;
